import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { getRentalPortalListing } from '../../../../api'
import {
  getPageBrowserTabTitleWithEntityName,
  useNotification,
  usePageBrowserTabTitle,
  useReloadRegistry,
} from '../../../../hooks'
import { withDraftProviders } from '../../../Draft'
import { useAsyncEntity } from '../../../Entity'
import { EntityPageProfileLayout } from '../../../Page'
import {
  getListingConflictNotification,
  getListingProfilePreparedData,
  LISTING_DRAFT_PREFIX,
  RentalPortalListingDraftProvider,
} from '../../../RentalPortalListing'

import { QA_NAME_LISTING_PROFILE_PAGE } from './RentalPortalListingProfile.constants'
import { ListingProfileContext } from './RentalPortalListingProfile.context'
import {
  getListingProfilePreviewOpenFullPageProps,
  getListingProfileTitle,
} from './RentalPortalListingProfile.utils'
import { RentalPortalListingProfileExpose } from './RentalPortalListingProfileExpose'
import { RentalPortalListingProfileHeader } from './RentalPortalListingProfileHeader'
import { useListingResponsibleManagerModalOpener } from './RentalPortalListingProfileHeaderInfoItems'
import { RentalPortalListingProfilePreviewError } from './RentalPortalListingProfilePreviewError'
import { RentalPortalListingProfileTabs } from './RentalPortalListingProfileTabs'
import { RentalPortalListingProfileWidgetList } from './RentalPortalListingProfileWidgetList'

function RentalPortalListingProfile({
  isPreview,
  layoutProps,
  listingId: listingIdProp,
  onClosePreview,
}) {
  const { listingId: listingIdFromParams } = useParams()
  const { t } = useTranslation()

  const { handleAddWarningNotification } = useNotification()

  const {
    handleReload: handleReloadDocumentsTab,
    registerReload: registerDocumentsTabReload,
  } = useReloadRegistry()

  const [modal, setModal] = useState(null)
  const [blockToEdit, setBlockToEdit] = useState(null)

  const listingId = listingIdProp ?? listingIdFromParams

  async function getProfileData() {
    return getRentalPortalListing(listingId)
  }

  const {
    entityData,
    handleEntityInit,
    handleEntityReload,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({
    entityId: listingId,
    getProfileData,
  })

  const listingData = getListingProfilePreparedData(entityData)

  usePageBrowserTabTitle({
    isEnabled: !isPreview,
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: getListingProfileTitle({ listingData, t }),
      title: t('RentalPortal.ListingProfileBrowserTabTitleKey'),
    }),
  })

  async function handleClickRetry() {
    await handleEntityInit()
  }

  function handleCloseModal() {
    setModal(null)
  }

  async function handleListingConflict(error) {
    handleCloseModal()

    handleAddWarningNotification(getListingConflictNotification({ error, t }))

    return handleEntityReload()
  }

  const { handleOpenResponsibleManagerModal } =
    useListingResponsibleManagerModalOpener({
      listingData,
      setModal,
      onClose: handleCloseModal,
      onConflict: handleListingConflict,
      onReload: handleEntityReload,
    })

  const openFullPageActionsProps =
    isPreview && listingId
      ? getListingProfilePreviewOpenFullPageProps({
          Actions: null,
          listingId,
          t,
          onClosePreview,
        })
      : null

  return isPreview && serverError ? (
    <RentalPortalListingProfilePreviewError
      openFullPageActionsProps={openFullPageActionsProps}
      onRetry={handleClickRetry}
    />
  ) : (
    <ListingProfileContext
      value={{
        blockToEdit,
        handleCloseModal,
        handleEntityReload,
        handleListingConflict,
        handleOpenResponsibleManagerModal,
        handleReloadDocumentsTab,
        isInitiated,
        isLoading,
        isPreview,
        listingData,
        registerDocumentsTabReload,
        setBlockToEdit,
        setModal,
      }}
    >
      <EntityPageProfileLayout
        {...layoutProps}
        Footer={isPreview ? null : undefined}
        Header={RentalPortalListingProfileHeader}
        headerProps={{ isPreview, openFullPageActionsProps }}
        qaName={QA_NAME_LISTING_PROFILE_PAGE}
        serverError={serverError}
        widgets={isPreview ? null : <RentalPortalListingProfileWidgetList />}
      >
        {isPreview ? (
          <RentalPortalListingProfileExpose />
        ) : (
          <RentalPortalListingProfileTabs />
        )}
        {modal}
      </EntityPageProfileLayout>
    </ListingProfileContext>
  )
}

export default withDraftProviders(RentalPortalListingProfile, [
  { Provider: RentalPortalListingDraftProvider, prefix: LISTING_DRAFT_PREFIX },
])
